// A dataset seen in place, so that looking at the data does not leave the
// place that pointed to it.
//
// - Inside a dialog (the publish dialog's list): the dataset slides in from
//   the right over the dialog's own view, inside the same dialog (`DatasetView`
//   with `back`), with "← <the dialog>" to return; no second dialog is laid
//   over the first. The host dialog is `relative` and keeps its own view
//   (its scroll, its preview) under it.
// - Elsewhere (a ui block's "Dataset" panel in a document): a dialog over the
//   place (`DatasetViewerLink`, `DatasetViewer`).
//
// Promise:
// - A plain click on the name opens it; a click with a modifier key or a
//   middle click goes to the dataset's screen as a link does.
// - It reads the dataset's copy (the same sync as its screen and any UI
//   reading it; nothing of its own): its facts, fields and rows. It writes
//   nothing (no row form, no tokens), whoever looks.
// - Going back (the back button, Escape inside a dialog) or closing (×,
//   Escape, the backdrop) leaves the place as it was. "Open dataset" goes to
//   the screen, and the place is told (`onLeave`: a dialog around closes).
// - On a phone the dialog of its own takes the whole screen and slides in
//   from the right, as a screen pushed on a phone.
// - In a phone app that draws the bar at the top, its bar is the app's (the
//   way back, "Open dataset" and close as the app's own buttons): the view
//   hands it as a header over the screen (`useShellHeader` with `over`), the
//   app's way back stepping back to the dialog, and draws none of its own.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useShellHeader } from "../shell/outside/outside";
import { useHostDrawsBar, usePhone } from "../shell/phone";
import { PlaceLink, usePlaceOpen } from "../shell/place-link";
import { isPlainClick } from "../shell/side-view";
import { ErrorLine, Skeleton } from "../ui/bits";
import { Button, buttonClass, IconButton } from "../ui/button";
import { Icon } from "../ui/icon";
import { Modal } from "../ui/modal";
import { DatasetContent, datasetLoadError, useDatasetCopy } from "./screen";

/** The dataset shown in place, as its opener names it. */
export interface DatasetInPlace {
  id: string;
  /** Its name before the copy comes. */
  title: string;
  /** Its screen; null: none here ("Open dataset" is left out). */
  to: string | null;
  /**
   * The place `to` is in, when not this one (a Workspace's slug): it opens
   * there as `PlaceLink` does (the desktop app opens that Workspace).
   */
  place?: string;
}

export function DatasetViewer({
  dataset,
  onClose,
  onLeave,
}: {
  /** null: closed. */
  dataset: DatasetInPlace | null;
  onClose(): void;
  onLeave?(): void;
}) {
  const phone = usePhone();
  const hostBar = useHostDrawsBar() && phone;
  return (
    <Modal
      open={dataset !== null}
      onClose={onClose}
      label={dataset?.title ?? m.datasets_viewer_open()}
      size={phone ? "full" : "wide"}
      hostBar={hostBar}
      className={
        hostBar
          ? ""
          : phone
            ? "pt-[env(safe-area-inset-top)]"
            : "flex max-h-[84vh] flex-col overflow-hidden"
      }
    >
      {dataset !== null && (
        <DatasetView dataset={dataset} onClose={onClose} onLeave={onLeave} />
      )}
    </Modal>
  );
}

/**
 * The dataset's view: a bar (back or its name, "Open dataset", close) and
 * what it holds, scrolling under the bar. With `back` it lies over the dialog
 * it was opened in (`OverDialog`); without, it fills a dialog of its own.
 */
export function DatasetView({
  dataset,
  onClose,
  onLeave,
  back,
}: {
  dataset: DatasetInPlace;
  onClose(): void;
  onLeave?(): void;
  /** Where it was opened from inside the same dialog (its name, and returning). */
  back?: { label: string; onBack(): void };
}) {
  const phone = usePhone();
  // The app's bar at the top shows the bar's buttons (see the top of the file).
  const hostBar = useHostDrawsBar() && phone;
  const openPlace = usePlaceOpen();
  const copy = useDatasetCopy(dataset.id);
  const dto = copy.data?.dto ?? null;
  const rows = copy.data?.rows;
  const title = dto?.title ?? dataset.title;
  // On a phone its own dialog is a screen pushed from the right.
  const top = useRef<HTMLDivElement>(null);
  useSlideIn(() =>
    phone && !back
      ? top.current?.closest<HTMLElement>("[data-modal-panel]")
      : null,
  );
  // Sliding back out to the right (then `back.onBack`).
  const [leaving, setLeaving] = useState(false);
  // What lies over the dialog (the app's edge swipe moves it).
  const over = useRef<HTMLDivElement>(null);
  const leave = () => {
    if (!back) return;
    if (reducedMotion()) back.onBack();
    else setLeaving(true);
  };
  const to = dataset.to;
  useShellHeader(
    hostBar
      ? {
          title,
          over: true,
          ...(back
            ? {
                // Slid off by the app's edge swipe: back at once.
                onBack: (how?: { slid: boolean }) =>
                  how?.slid ? back.onBack() : leave(),
                layer: () => over.current,
              }
            : {}),
          actions: [
            ...(to
              ? [
                  {
                    id: "dataset-open",
                    label: m.datasets_viewer_open(),
                    style: "pill" as const,
                    onPress: () => {
                      onClose();
                      onLeave?.();
                      openPlace(to, dataset.place ?? "me");
                    },
                  },
                ]
              : []),
            {
              id: "dataset-close",
              label: m.datasets_viewer_close(),
              icon: "close",
              onPress: onClose,
            },
          ],
        }
      : null,
  );
  const bar = (
    <div className="flex min-h-8 min-w-0 items-center gap-2">
      {back ? (
        <Button
          variant="ghost"
          onClick={leave}
          data-dataset-back=""
          className="-ml-2 min-w-0 gap-1 px-2"
        >
          <Icon kind="back" size={14} />
          <span className="truncate">{back.label}</span>
        </Button>
      ) : (
        <h2 className="min-w-0 truncate text-[15px] font-normal text-fg">
          {title}
        </h2>
      )}
      <span className="flex-1" />
      {dataset.to && (
        <PlaceLink
          href={dataset.to}
          place={dataset.place ?? "me"}
          onClick={() => {
            onClose();
            onLeave?.();
          }}
          className={buttonClass("ghost")}
        >
          {m.datasets_viewer_open()}
        </PlaceLink>
      )}
      <IconButton label={m.datasets_viewer_close()} onClick={onClose}>
        <Icon kind="close" size={15} />
      </IconButton>
    </div>
  );
  const body = copy.isPending ? (
    <Skeleton lines={[40, 70, 55]} />
  ) : copy.isError || !dto || !rows ? (
    <ErrorLine>{datasetLoadError(copy.error)}</ErrorLine>
  ) : (
    <DatasetContent
      id={dataset.id}
      dto={dto}
      rows={rows}
      writable={false}
      titled={false}
    />
  );

  if (back)
    return (
      <OverDialog
        bar={hostBar ? null : bar}
        title={title}
        body={body}
        onBack={leave}
        leaving={leaving}
        onLeft={back.onBack}
        layerRef={over}
      />
    );
  return (
    <>
      {!hostBar && (
        <div
          ref={top}
          className={`flex-none ${phone ? "px-4 pt-3 pb-2" : "pb-3"}`}
        >
          {bar}
        </div>
      )}
      {/* The body scrolls under the bar, out to the dialog's edges. */}
      <div
        ref={hostBar ? top : undefined}
        className={`panel-scroll min-h-0 flex-1 select-text overflow-y-auto overscroll-contain ${phone ? "px-4 pb-[calc(24px+env(safe-area-inset-bottom))]" : "-mx-5 -mb-5 px-5 pb-6"} ${hostBar ? "pt-[var(--top-dock-clearance)]" : ""}`}
      >
        {hostBar && (
          <h2 className="mt-1 mb-1.5 text-[17px] font-normal text-fg">
            {title}
          </h2>
        )}
        {body}
      </div>
    </>
  );
}

const isSlideOut = (a: Animation) =>
  (a as Animation & { animationName?: string }).animationName ===
  "slide-out-right";

export const reducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/**
 * Slides an element in from the right once its first frame is drawn: a CSS
 * animation started with the render, and drawing a long table ate the slide.
 */
export function useSlideIn(target: () => HTMLElement | null | undefined) {
  const get = useRef(target);
  // frontend-development: not a fetch; moves what was just drawn.
  useLayoutEffect(() => {
    const el = get.current();
    if (!el?.animate || reducedMotion()) return;
    el.style.transform = "translateX(100%)";
    let id = requestAnimationFrame(() => {
      id = requestAnimationFrame(() => {
        el.style.transform = "";
        el.animate([{ transform: "translateX(100%)" }, { transform: "none" }], {
          duration: 200,
          easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
        });
      });
    });
    return () => {
      cancelAnimationFrame(id);
      el.style.transform = "";
    };
  }, []);
}

/**
 * The dataset over the dialog's own view, as a screen pushed on a phone: it
 * slides in from the right over the whole dialog (which stays as it was
 * under it), and back out to the right. It scrolls under its bar by itself.
 * Escape inside goes back (it does not close the dialog).
 */
function OverDialog({
  bar,
  title,
  body,
  onBack,
  leaving,
  onLeft,
  layerRef,
}: {
  /** null: the app's bar at the top is its bar. */
  bar: ReactNode;
  title: string;
  body: ReactNode;
  onBack(): void;
  leaving: boolean;
  onLeft(): void;
  /** The layer itself, for what moves it from outside (the app's edge swipe). */
  layerRef?: RefObject<HTMLDivElement | null>;
}) {
  const phone = usePhone();
  const own = useRef<HTMLDivElement>(null);
  const layer = layerRef ?? own;
  useSlideIn(() => layer.current);
  // frontend-development: not a fetch; with nothing sliding out (motion
  // reduced by the page, no animations where it runs) it is gone at once.
  useLayoutEffect(() => {
    if (!leaving) return;
    const running = layer.current?.getAnimations?.() ?? [];
    // (The shadow at its left edge shows only while it slides: at rest it
    // falls outside the dialog, which clips it.)
    // A slide in still running gives way to the slide out.
    for (const a of running) if (!isSlideOut(a)) a.cancel();
    if (!running.some(isSlideOut)) onLeft();
  }, [leaving, onLeft, layer]);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: catches Escape from the buttons and fields inside
    <div
      ref={layer}
      className={`absolute inset-0 z-10 flex flex-col bg-surface shadow-[-8px_0_24px_rgba(0,0,0,0.12)] ${phone ? "" : "rounded-[11px]"} ${leaving ? "slide-out-right" : ""}`}
      onAnimationEnd={(e) => {
        if (leaving && e.target === e.currentTarget) onLeft();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        onBack();
      }}
    >
      {bar !== null && (
        <div
          className={`flex-none ${phone ? "px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-2" : "px-5 pt-5 pb-2"}`}
        >
          {bar}
        </div>
      )}
      <div
        className={`panel-scroll min-h-0 flex-1 select-text overflow-y-auto overscroll-contain ${phone ? "px-4 pb-[calc(24px+env(safe-area-inset-bottom))]" : "px-5 pb-6"} ${bar === null ? "pt-[var(--top-dock-clearance)]" : ""}`}
      >
        <h2 className="mt-1 mb-1.5 text-[17px] font-normal text-fg">{title}</h2>
        {body}
      </div>
    </div>
  );
}

/**
 * A dataset's name that opens it in place (see the top of the file): through
 * `onView` when the place shows it itself (a dialog turning to it), else in a
 * dialog of its own. With no screen to go to (`to` null), a button.
 */
export function DatasetViewerLink({
  dataset,
  onView,
  onLeave,
  className,
  children,
}: {
  dataset: DatasetInPlace;
  onView?(): void;
  onLeave?(): void;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const show = () => (onView ? onView() : setOpen(true));
  const onLinkClick = (e: MouseEvent) => {
    if (!isPlainClick(e)) return;
    e.preventDefault();
    show();
  };
  return (
    <>
      {dataset.to && dataset.place && dataset.place !== "me" ? (
        // In another place: a modified click opens it there as a link does.
        <a href={dataset.to} onClick={onLinkClick} className={className}>
          {children}
        </a>
      ) : dataset.to ? (
        <Link to={dataset.to} onClick={onLinkClick} className={className}>
          {children}
        </Link>
      ) : (
        <button
          type="button"
          onClick={show}
          className={`cursor-pointer text-left ${className ?? ""}`}
        >
          {children}
        </button>
      )}
      {!onView && (
        <DatasetViewer
          dataset={open ? dataset : null}
          onClose={() => setOpen(false)}
          onLeave={onLeave}
        />
      )}
    </>
  );
}
